import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Zap, RefreshCw, CheckCircle, XCircle, Webhook, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';

interface Hook {
  id: string;
  eventType: string;
  url: string;
  isActive: boolean;
  description?: string;
  createdAt: string;
}

interface EventType {
  type: string;
  description: string;
  category: string;
}

export default function WebhooksPanel() {
  const [hooks, setHooks] = useState<Hook[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string }>>({});

  // Form state
  const [formUrl, setFormUrl] = useState('');
  const [formEventType, setFormEventType] = useState('*');
  const [formSecret, setFormSecret] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchHooks = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; hooks: Hook[] }>('/hooks');
      if (res.data?.success) setHooks(res.data.hooks || []);
    } catch (err) {
      console.error('Failed to fetch hooks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEventTypes = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; events: EventType[] }>('/hooks/events');
      if (res.data?.success) setEventTypes(res.data.events || []);
    } catch (err) {
      console.error('Failed to fetch event types:', err);
    }
  }, []);

  useEffect(() => {
    fetchHooks();
    fetchEventTypes();
  }, [fetchHooks, fetchEventTypes]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formUrl || !formSecret) return;
    setSubmitting(true);
    try {
      const res = await api.post<{ success: boolean }>('/hooks', {
        eventType: formEventType,
        url: formUrl,
        secret: formSecret,
        description: formDescription,
      });
      if (res.data?.success) {
        setShowForm(false);
        setFormUrl('');
        setFormSecret('');
        setFormDescription('');
        setFormEventType('*');
        fetchHooks();
      }
    } catch (err) {
      console.error('Failed to create hook:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/hooks/${id}`);
      setHooks(hooks.filter(h => h.id !== id));
    } catch (err) {
      console.error('Failed to delete hook:', err);
    }
  };

  const handleTest = async (id: string) => {
    try {
      const res = await api.post<{ success: boolean }>(`/hooks/test/${id}`);
      setTestResults(prev => ({
        ...prev,
        [id]: { success: !!res.data?.success, message: res.data?.success ? 'Ping sent successfully' : 'Ping failed' },
      }));
      setTimeout(() => {
        setTestResults(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }, 3000);
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [id]: { success: false, message: 'Request failed' },
      }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Webhook size={20} className="text-hig-accent" />
          <div>
            <h2 className="text-lg font-semibold text-hig-text-primary">Webhooks</h2>
            <p className="text-sm text-hig-text-secondary">Receive real-time notifications for governance events</p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-hig-accent text-white rounded-xl text-sm font-medium hover:bg-hig-accent/90 transition-colors"
        >
          <Plus size={16} />
          Add Webhook
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <form onSubmit={handleCreate} className="card p-5 space-y-4 border-2 border-hig-accent/20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="webhook-url" className="block text-sm font-medium text-hig-text-primary mb-1">Endpoint URL</label>
              <input
                id="webhook-url"
                type="url"
                value={formUrl}
                onChange={(e) => setFormUrl(e.target.value)}
                placeholder="https://your-server.com/webhook"
                className="w-full px-3 py-2 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent"
                required
              />
            </div>
            <div>
              <label htmlFor="webhook-secret" className="block text-sm font-medium text-hig-text-primary mb-1">Secret Key</label>
              <input
                id="webhook-secret"
                type="password"
                value={formSecret}
                onChange={(e) => setFormSecret(e.target.value)}
                placeholder="whsec_..."
                className="w-full px-3 py-2 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent"
                required
              />
            </div>
            <div>
              <label htmlFor="webhook-event-type" className="block text-sm font-medium text-hig-text-primary mb-1">Event Type</label>
              <div className="relative">
                <select
                  id="webhook-event-type"
                  value={formEventType}
                  onChange={(e) => setFormEventType(e.target.value)}
                  className="w-full px-3 py-2 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent appearance-none"
                >
                  <option value="*">All events (*)</option>
                  {eventTypes.map((et) => (
                    <option key={et.type} value={et.type}>
                      {et.type} — {et.description}
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-hig-text-tertiary pointer-events-none" />
              </div>
            </div>
            <div>
              <label htmlFor="webhook-description" className="block text-sm font-medium text-hig-text-primary mb-1">Description (optional)</label>
              <input
                id="webhook-description"
                type="text"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="e.g. Slack alerts"
                className="w-full px-3 py-2 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 justify-end">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 bg-hig-accent text-white rounded-lg text-sm font-medium hover:bg-hig-accent/90 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Creating...' : 'Create Webhook'}
            </button>
          </div>
        </form>
      )}

      {/* Hooks List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={20} className="animate-spin text-hig-text-tertiary" />
        </div>
      ) : hooks.length === 0 ? (
        <div className="card p-8 text-center">
          <Webhook size={32} className="mx-auto text-hig-text-tertiary mb-3" />
          <p className="text-hig-text-secondary text-sm">No webhooks configured yet</p>
          <p className="text-hig-text-tertiary text-xs mt-1">Add a webhook to receive real-time event notifications</p>
        </div>
      ) : (
        <div className="space-y-3">
          {hooks.map((hook) => (
            <div key={hook.id} className="card p-4 flex items-center gap-4">
              <div className={`w-2.5 h-2.5 rounded-full ${hook.isActive ? 'bg-green-500' : 'bg-hig-text-tertiary'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-hig-text-primary truncate">{hook.url}</p>
                  <span className="px-2 py-0.5 bg-hig-bg-tertiary text-hig-text-secondary text-xs rounded-full whitespace-nowrap">
                    {hook.eventType}
                  </span>
                </div>
                {hook.description && (
                  <p className="text-xs text-hig-text-tertiary mt-0.5">{hook.description}</p>
                )}
              </div>

              {/* Test result */}
              {testResults[hook.id] && (
                <div className={`flex items-center gap-1 text-xs ${testResults[hook.id].success ? 'text-green-500' : 'text-hig-error'}`}>
                  {testResults[hook.id].success ? <CheckCircle size={14} /> : <XCircle size={14} />}
                  {testResults[hook.id].message}
                </div>
              )}

              <button
                onClick={() => handleTest(hook.id)}
                className="p-2 text-hig-text-tertiary hover:text-hig-accent hover:bg-hig-accent/10 rounded-lg transition-colors"
                title="Send test ping"
                aria-label="Send test ping"
              >
                <Zap size={16} />
              </button>
              <button
                onClick={() => handleDelete(hook.id)}
                className="p-2 text-hig-text-tertiary hover:text-hig-error hover:bg-hig-error/10 rounded-lg transition-colors"
                title="Delete webhook"
                aria-label="Delete webhook"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
